import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import {
  lpgCommit,
  lpgPreview,
  lpgSurvey,
  type LoadMode,
  type LpgCommitResponse,
  type LpgPreviewResponse,
  type LpgSurvey,
} from '@/lib/api';
import { ConfirmGraphReplaceModal } from '@/components/ConfirmGraphReplaceModal';
import { GraphSelector } from '@/components/GraphSelector';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { Message } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

// ---------------------------------------------------------------------------
// Property-graph import (Neo4j over its HTTP endpoint).
//
// Survey → Preview → Commit, in that order and for a reason: the survey says
// what the database holds without reading a row, so it answers on a graph too
// large to bring across; the preview reads it once and the server keeps what
// it read, so committing does not read it again.
//
// A relationship's properties come across as details on the fact itself,
// which is the thing this import does that a file-based one cannot.
// ---------------------------------------------------------------------------

type Step = 'connect' | 'surveyed' | 'previewed' | 'done';

const DEFAULT_URL = 'http://localhost:7474';
const DEFAULT_DATABASE = 'neo4j';
const DEFAULT_GRAPH = 'urn:tres:data';

export function LpgImportPanel() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? '';
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('connect');
  const [url, setUrl] = useState(DEFAULT_URL);
  const [database, setDatabase] = useState(DEFAULT_DATABASE);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [key, setKey] = useState('');
  const [normalize, setNormalize] = useState(false);
  const [graph, setGraph] = useState<string | null>(DEFAULT_GRAPH);
  const [mode, setMode] = useState<LoadMode>('append');
  const [confirmReplace, setConfirmReplace] = useState(false);

  const [survey, setSurvey] = useState<LpgSurvey | null>(null);
  const [preview, setPreview] = useState<LpgPreviewResponse | null>(null);
  const [result, setResult] = useState<LpgCommitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const connection = {
    url,
    database,
    username: username.trim() || undefined,
    password: password || undefined,
  };

  function resetFrom(next: Step) {
    setError(null);
    setResult(null);
    if (next === 'connect') {
      setSurvey(null);
      setPreview(null);
    }
    if (next === 'surveyed') setPreview(null);
    setStep(next);
  }

  async function runSurvey() {
    if (!token) return;
    setBusy(true);
    setError(null);
    setPreview(null);
    setResult(null);
    const r = await lpgSurvey(connection, token);
    setBusy(false);
    if (r.ok) {
      setSurvey(r.data);
      setStep('surveyed');
    } else {
      setError(r.message);
    }
  }

  async function runPreview() {
    if (!token) return;
    setBusy(true);
    setError(null);
    setResult(null);
    const r = await lpgPreview(
      {
        ...connection,
        key: key.trim() || undefined,
        normalize_predicates: normalize,
      },
      token,
    );
    setBusy(false);
    if (r.ok) {
      setPreview(r.data);
      setStep('previewed');
    } else {
      setError(r.message);
    }
  }

  async function runCommit() {
    if (!token || !preview || !graph) return;
    setBusy(true);
    setError(null);
    const r = await lpgCommit(
      { plan_id: preview.plan_id, graph, mode },
      token,
    );
    setBusy(false);
    setConfirmReplace(false);
    if (r.ok) {
      setResult(r.data);
      // The plan is used up, so the button cannot write the same graph twice.
      setPreview(null);
      setStep('done');
      invalidateAfterDataWrite(queryClient);
    } else {
      setError(r.message);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-tres-text-secondary">
        Read a Neo4j database and bring it across as knowledge. Each node
        becomes an entity carrying its properties, and each relationship
        becomes a fact between two entities — with the relationship&rsquo;s own
        properties kept as details on that fact. Nothing is written until you
        have seen what would be written.
      </p>

      {error && <Message kind="warning">{error}</Message>}

      <div className="space-y-3 rounded-md border border-border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-tres-text-secondary">Address</span>
            <input
              className="mt-1 w-full rounded border border-border px-2 py-1"
              value={url}
              disabled={busy}
              onChange={(e) => {
                setUrl(e.target.value);
                resetFrom('connect');
              }}
              placeholder={DEFAULT_URL}
            />
          </label>
          <label className="block text-sm">
            <span className="text-tres-text-secondary">Database</span>
            <input
              className="mt-1 w-full rounded border border-border px-2 py-1"
              value={database}
              disabled={busy}
              onChange={(e) => {
                setDatabase(e.target.value);
                resetFrom('connect');
              }}
              placeholder={DEFAULT_DATABASE}
            />
          </label>
          <label className="block text-sm">
            <span className="text-tres-text-secondary">
              User <span className="font-normal">(leave blank if the database has no sign-in)</span>
            </span>
            <input
              className="mt-1 w-full rounded border border-border px-2 py-1"
              value={username}
              disabled={busy}
              autoComplete="off"
              onChange={(e) => {
                setUsername(e.target.value);
                resetFrom('connect');
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="text-tres-text-secondary">Password</span>
            <input
              type="password"
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-border px-2 py-1"
              value={password}
              disabled={busy}
              onChange={(e) => {
                setPassword(e.target.value);
                resetFrom('connect');
              }}
            />
          </label>
        </div>
        <p className="text-xs text-tres-text-secondary">
          These details are used for this import and are not kept.
        </p>

        {/* Actions on the LEFT (design template). */}
        <div className="flex items-center gap-2 pt-1">
          <button
            className={buttonVariants({ variant: 'primary', size: 'md' })}
            disabled={busy || !url.trim() || !database.trim()}
            onClick={() => void runSurvey()}
          >
            {busy && step === 'connect' ? 'Reading…' : 'See what it holds'}
          </button>
        </div>
      </div>

      {survey && (
        <div className="space-y-3 rounded-md border border-border bg-card p-4">
          <h3 className="text-sm font-semibold">
            {survey.nodes_total.toLocaleString()} things,{' '}
            {survey.relationships_total.toLocaleString()} connections
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h4 className="text-sm text-tres-text-secondary">Kinds of thing</h4>
              <ul className="mt-1 max-h-48 overflow-auto text-sm">
                {survey.node_labels.map((l) => (
                  <li key={l.label} className="flex justify-between gap-4">
                    <span>{l.label}</span>
                    <span className="tabular-nums text-tres-text-secondary">
                      {l.nodes.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm text-tres-text-secondary">Kinds of connection</h4>
              <ul className="mt-1 max-h-48 overflow-auto text-sm">
                {survey.relationship_types.map((t) => (
                  <li key={t.relationship_type} className="flex justify-between gap-4">
                    <span>{t.relationship_type}</span>
                    <span className="tabular-nums text-tres-text-secondary">
                      {t.relationships.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {survey.relationship_properties.length > 0 && (
            <div>
              <h4 className="text-sm text-tres-text-secondary">
                Details carried on connections — these come across on the fact itself
              </h4>
              <ul className="mt-1 max-h-32 overflow-auto text-sm">
                {survey.relationship_properties.map((p) => (
                  <li key={`${p.owner}.${p.key}`}>
                    <span className="font-medium">{p.owner}</span> · {p.key}{' '}
                    <span className="text-tres-text-secondary">{p.types.join(' or ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="text-tres-text-secondary">
                Which property identifies a thing
              </span>
              <input
                className="mt-1 w-full rounded border border-border px-2 py-1"
                value={key}
                disabled={busy}
                onChange={(e) => {
                  setKey(e.target.value);
                  resetFrom('surveyed');
                }}
                placeholder="leave blank to use the database's own id"
              />
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={normalize}
                disabled={busy}
                onChange={(e) => {
                  setNormalize(e.target.checked);
                  resetFrom('surveyed');
                }}
              />
              <span>Treat WORKS_FOR and worksFor as the same connection</span>
            </label>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              className={buttonVariants({ variant: 'primary', size: 'md' })}
              disabled={busy}
              onClick={() => void runPreview()}
            >
              {busy && step === 'surveyed' ? 'Reading the graph…' : 'Preview'}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-3 rounded-md border border-border bg-card p-4">
          <h3 className="text-sm font-semibold">
            {preview.statements.toLocaleString()} facts would be written
          </h3>
          <p className="text-sm text-tres-text-secondary">
            {preview.value_statements.toLocaleString()} about things,{' '}
            {preview.relationship_statements.toLocaleString()} between them,
            carrying {preview.tags.toLocaleString()} details. Read from{' '}
            {preview.nodes_read.toLocaleString()} things and{' '}
            {preview.relationships_read.toLocaleString()} connections.
          </p>

          {preview.findings.length > 0 && (
            <div>
              <h4 className="text-sm text-tres-text-secondary">
                What the import found, and what it will do
              </h4>
              <ul className="mt-1 max-h-64 space-y-1 overflow-auto text-sm">
                {preview.findings.map((f, i) => (
                  <li key={i} className="border-b border-border pb-1 last:border-0">
                    <span className="font-medium">{f.kind}</span> — {f.detail}
                    <span className="block text-tres-text-secondary">{f.resolution}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.sample.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm text-tres-text-secondary">
                The first {preview.sample.length} facts
              </summary>
              <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 text-xs">
                {preview.sample.join('\n')}
              </pre>
            </details>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="text-sm">
              <span className="text-tres-text-secondary">Write to</span>
              <div className="mt-1">
                <GraphSelector value={graph} onChange={setGraph} />
              </div>
            </div>
            <label className="block text-sm">
              <span className="text-tres-text-secondary">How</span>
              <select
                className="mt-1 w-full rounded border border-border px-2 py-1"
                value={mode}
                disabled={busy}
                onChange={(e) => setMode(e.target.value as LoadMode)}
              >
                <option value="append">Add to what is there</option>
                <option value="replace">Replace what is there</option>
              </select>
            </label>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              className={buttonVariants({ variant: 'primary', size: 'md' })}
              disabled={busy || !graph}
              onClick={() => {
                if (mode === 'replace') setConfirmReplace(true);
                else void runCommit();
              }}
            >
              {busy ? 'Writing…' : 'Commit'}
            </button>
            <button
              className={buttonVariants({ variant: 'secondary', size: 'md' })}
              disabled={busy}
              onClick={() => resetFrom('surveyed')}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {result && (
        <Message kind="platform">
          Wrote{' '}
          {result.statements_written === null
            ? `${result.statements_planned.toLocaleString()} facts`
            : `${result.statements_written.toLocaleString()} facts`}{' '}
          to {result.graph}
          {result.replaced ? ', replacing what was there' : ''}, in{' '}
          {(result.elapsed_ms / 1000).toFixed(1)} s. They are recorded as coming
          from {result.source}, so this import can be removed on its own.
        </Message>
      )}

      {confirmReplace && graph && (
        <ConfirmGraphReplaceModal
          graph={graph}
          what={`this property graph (${preview?.statements.toLocaleString() ?? 'the previewed'} facts)`}
          onCancel={() => setConfirmReplace(false)}
          onConfirm={() => void runCommit()}
        />
      )}
    </div>
  );
}
